import {
  BackendServerActionType,
  ButtonElementModel,
  CastUtility,
  DevelopDialogScript,
  DevelopScriptServicePack,
  FetchListResponseDataFormat,
  InputPartsBaseModel,
  LinkElementModel,
  ListPanelElementModel,
  MessageService,
  PanelModel,
  PanelType,
  PartsBaseModel,
  ResponseDataModel,
  ValidateCheckUtility,
  ViewType,
} from '@ktvs8/gocoo-engine-front-end';

/**
 * 発注内容選択ダイアログのスクリプト
 */
export class MProgramOrderDialogConditionList implements DevelopDialogScript {
  readonly scriptName: string = 'MProgramOrderDialogConditionList';

  constructor() {}

  /**
   * ダイアログが表示された際の初期処理
   * @param panelModels
   * @param service
   * @param receivedData MProgramTicketKey
   */
  init(panelModels: Map<PanelType, PanelModel>, service: DevelopScriptServicePack, receivedData: any): void {
    if (ValidateCheckUtility.isEmpty(receivedData)) {
      service.dialogService.closeViewDialog(null);
    }
    this.search(receivedData, panelModels, service);
  }

  /**
   * ダイアログで何らかのボタンが押下された際の処理
   * @param panelModels パネルモデルのマップ
   * @param clickedButtonElement 押下されたボタンのモデル
   * @param service サービス
   * @returns
   */
  clickedButton(
    panelModels: Map<PanelType, PanelModel>,
    clickedButtonElement: ButtonElementModel,
    service: DevelopScriptServicePack
  ) {}

  /**
   * 独自のバリデーションチェック
   * @param panelModels
   * @returns
   */
  validationCheck(
    targetValidationPanel: PanelType,
    actionType: BackendServerActionType,
    panelModels: Map<PanelType, PanelModel>,
    service: DevelopScriptServicePack
  ): boolean {
    return true;
  }
  /**
   * input-partsのイベント
   * @param inputPartsModel
   * @param panelModels
   * @param service
   */
  inputPartsEvent(
    inputPartsModel: InputPartsBaseModel,
    panelModels: Map<PanelType, PanelModel>,
    service: DevelopScriptServicePack
  ) {
    // 委託先のイベントが発生
    if (inputPartsModel.id === 'MSupplierTicketKey') {
      return;
    }
  }

  /**
   * リンクが押下されたときの処理
   * @param panelModels パネルモデルのマップ
   * @param clickedLinkElement 押下されたリンクモデル
   * @param service スクリプトのサービス
   */
  clickedLink(
    panelModels: Map<PanelType, PanelModel>,
    clickedLinkElement: LinkElementModel,
    service: DevelopScriptServicePack
  ) {
    if (clickedLinkElement.id.includes('Content')) {
      const result = this.getSelectedData(panelModels, clickedLinkElement);
      service.dialogService.closeViewDialog(result);
      return;
    }
  }

  returnParent(
    panelModels: Map<PanelType, PanelModel>,
    clickedLinkElement: LinkElementModel,
    service: DevelopScriptServicePack
  ) {}
  /**
   * 選択されたチケットキーを取得する
   * @param panelModels
   * @param clickedLinkElement
   * @returns
   */
  getSelectedData(
    panelModels: Map<PanelType, PanelModel>,
    clickedLinkElement: LinkElementModel
  ): MProgramOrderSelectedResult {
    const index = this.getIndex(clickedLinkElement);
    const listPanel = panelModels.get(PanelType.ListPanel) as ListPanelElementModel;
    const row = listPanel.listElementModel.bodyModel.rows[index];
    const content = row.getCell('Content');

    if (content === undefined) {
      throw new Error('Content is undefined');
    }

    return <MProgramOrderSelectedResult>{
      ticketKey: row.commonDBValue.ticketKey,
      content: content.cellModel.value,
    };
  }

  /**
   * 番組(MProgram)に紐づく発注内容(MProgramOrder)を検索する
   *
   * ## 検索条件
   * - MProgramTicketKey
   *
   * ## 検索後の処理
   * - 取得した検索結果から、番組に紐づく発注内容（サブチケット）を取得する
   * - サブチケットのMProgramOrderを取得する
   * - リストパネルに取得したMProgramOrderを設定する
   * - ページネーションを設定する
   *
   * @param programName 画面で入力された番組名
   * @param panelModels
   * @param service
   */
  search(mProgramTicketKey: string, panelModels: Map<PanelType, PanelModel>, service: DevelopScriptServicePack) {
    // 検索処理
    const condition = {
      TicketKey: mProgramTicketKey,
    };

    /**
     * コールバック関数
     * 一覧の行を作成
     * @param responseData
     * @returns
     */
    const setList = (responseData: ResponseDataModel) => {
      if (!responseData.isCompleted) {
        return;
      }

      const listPanel = panelModels.get(PanelType.ListPanel) as ListPanelElementModel;

      // 取得した検索結果から、番組に紐づく発注内容（サブチケット）を取得する
      const dataList = (responseData: ResponseDataModel) => {
        if (
          ValidateCheckUtility.isEmpty(responseData.data) ||
          ValidateCheckUtility.isEmpty(responseData.data['Subtickets']) ||
          ValidateCheckUtility.isEmpty(responseData.data['Subtickets']['MProgramOrder'])
        ) {
          return [];
        }

        // 発注内容選択単体のチケットキーにRowIndexが含まれていないためここでRowIndex昇順にソート
        return responseData.data['Subtickets']['MProgramOrder'].sort(
          (a: { RowIndex: any }, b: { RowIndex: any }) => a.RowIndex - b.RowIndex
        );
      };

      // レスポンスデータを設定
      responseData.data = {
        DataList: dataList(responseData),
        LastPage: true,
        NextPaginationStartKey: '',
        HitsRequestLimiter: true,
      };

      // リストパネルに取得したMProgramOrderを設定する
      listPanel.listElementModel.bodyModel.buildRowsByFetchData(
        responseData,
        listPanel.listElementModel.headerModel.headers,
        new MessageService()
      );

      // ページ遷移の情報をセット
      const fetchData = new FetchListResponseDataFormat();
      fetchData.setParam(responseData);
      listPanel.paginationElementModel.setupPaginationList(fetchData, condition);
      listPanel.paginationElementModel.isDisabledSelectedPageSize = true;
    };

    service.dataTransmissionService.fetchRecord(ViewType.List, 'MProgram', condition, setList, false);
  }

  /**
   * 行数を取得する
   * @param element
   * @returns
   */
  getIndex(element: PartsBaseModel): number {
    return CastUtility.anyToNumber(element.id.split('_')[1]);
  }
}

/**
 * ダイアログの選択結果
 *
 * 親に返却するデータの型を定義する
 */
export interface MProgramOrderSelectedResult {
  ticketKey: string;
  content: string;
}
